import { denverEvening, livesInMonth, YOUTUBE_LIVE_URL } from "@/lib/schedule";

/* Sample data for the club portal prototype (/blue-2/portal).

   The model follows the research brief (portal-research.md §5): the monthly
   Project is the unit of the club; each Project has Sessions (Prep → Class 1
   → Class 2) whose state runs Upcoming → Tonight → Live → Processing → Replay;
   short Notes from Chelsea replace Quips and logistics posts; Posts are the
   leftover long-form extras.

   Project names (Witch Hat, Skyline Dress, Color Block Dress, Cooler Bag,
   Sunny Side Tank) and the Witch Hat / Skyline supply lists and fixes come
   from the real Patreon. Everything else is invented: member names, chat,
   questions, codes, lengths, chapters. Nothing here talks to a server.

   Times are real instants. The prototype's "clock" (see CLOCKS) picks a
   simulated "now" around tonight's Witch Hat Class 1 (Tue Oct 13 2026,
   7 PM MT); every state is derived from that instant with sessionState(). */

/* ─────────────────────────── Roles & access ─────────────────────────── */

export type Role = "visitor" | "free" | "friend" | "member" | "chelsea";
const RANK: Record<Role, number> = { visitor: 0, free: 1, friend: 2, member: 3, chelsea: 4 };

export const ROLES: { key: Role; label: string; short: string; note: string }[] = [
  { key: "visitor", label: "Visitor", short: "Visitor", note: "Not signed in" },
  { key: "free", label: "Free member", short: "Free", note: "Free account · notes & YouTube lives" },
  { key: "friend", label: "Thread Friend", short: "Thread Friend", note: "$3/mo · posts & notes, no classes" },
  { key: "member", label: "Member", short: "Member", note: "$25/mo · everything" },
  { key: "chelsea", label: "Chelsea", short: "Chelsea", note: "Creator view" },
];

export type Plan = "free" | "friend" | "member";
export const PLANS: Record<Plan, { key: Plan; name: string; price: number; per: string; tag: string; perks: string[] }> = {
  free: { key: "free", name: "Free", price: 0, per: "Free", tag: "Free",
    perks: ["Notes from Chelsea", "Free YouTube lives (most Tuesdays)", "Read the community"] },
  friend: { key: "friend", name: "Thread Friend", price: 3, per: "$3/mo", tag: "Thread Friend ($3)",
    perks: ["Everything in Free", "Studio posts & behind the scenes", "Post in the community & share makes"] },
  member: { key: "member", name: "Member", price: 25, per: "$25/mo", tag: "Member ($25)",
    perks: ["Everything in Thread Friend", "2 live classes a month with Chelsea", "Every replay, with chapters & fix notes", "Pattern discount codes & supply lists", "Ask questions on any class"] },
};
/** Annual billing saves about 17% (two months free), as on Patreon. */
export const ANNUAL = { member: 250, friend: 30, save: "Save 17%" };

/** Things a role can open. The minimum role for each is in NEED. */
export type Thing =
  | "schedule" | "teaser" | "youtube" | "preview"                       // anyone
  | "notes" | "supplies" | "communityRead" | "freePosts"                // free account
  | "posts" | "communityPost" | "makesPost"                             // Thread Friend
  | "code" | "live" | "replays" | "chatReplay" | "qaAsk" | "calendarFeed" // Member
  | "manage";                                                           // Chelsea
export const NEED: Record<Thing, Role> = {
  schedule: "visitor", teaser: "visitor", youtube: "visitor", preview: "visitor",
  notes: "free", supplies: "free", communityRead: "free", freePosts: "free",
  posts: "friend", communityPost: "friend", makesPost: "friend",
  code: "member", live: "member", replays: "member", chatReplay: "member", qaAsk: "member", calendarFeed: "member",
  manage: "chelsea",
};
/** Can this role open this thing? `can("friend", "replays") === false`. */
export const can = (role: Role, thing: Thing) => RANK[role] >= RANK[NEED[thing]];
/** Is role a at least role b? */
export const atLeast = (a: Role, b: Role) => RANK[a] >= RANK[b];
/** The plan that unlocks a thing (Chelsea-only things return null). */
export function planFor(thing: Thing): Plan | null {
  const n = NEED[thing];
  return n === "chelsea" ? null : n === "visitor" ? "free" : n;
}
/** The plan a role is on (visitor → null, chelsea → member). */
export const planOf = (role: Role): Plan | null => (role === "visitor" ? null : role === "chelsea" ? "member" : role);

/* ─────────────────────────── People ─────────────────────────── */

export type Person = { id: string; name: string; initials: string; color: string; avatar?: string; badge?: "chelsea" | "founding"; city?: string };
/** Avatar fills; every one takes ink (#0B302A) text at 4.5:1 or more. */
const FILLS = ["#C0C7FB", "#F0B0B0", "#F0BE3C", "#E66E78", "#B9D8C9", "#F3D3A6"];
const person = (id: string, name: string, i: number, extra: Partial<Person> = {}): Person =>
  ({ id, name, initials: name.split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase(), color: FILLS[i % FILLS.length], ...extra });

export const CHELSEA: Person = person("chelsea", "Chelsea", 3, { avatar: "/img/chelsea.jpeg", badge: "chelsea", city: "Utah" });
export const PEOPLE: Record<string, Person> = Object.fromEntries([
  CHELSEA,
  person("maggie", "Maggie L.", 0, { badge: "founding", city: "Boise, ID" }),
  person("dee", "Dee R.", 1, { city: "Tulsa, OK" }),
  person("tasha", "Tasha K.", 2, { badge: "founding", city: "Columbus, OH" }),
  person("linh", "Linh N.", 4, { city: "San Jose, CA" }),
  person("bev", "Bev O.", 5, { badge: "founding", city: "Ogden, UT" }),
  person("carmen", "Carmen V.", 0, { city: "El Paso, TX" }),
  person("jill", "Jill S.", 1, { city: "Halifax, NS" }),
  person("noor", "Noor A.", 2, { city: "Denver, CO" }),
  person("paula", "Paula G.", 4, { city: "Spokane, WA" }),
  person("ruthie", "Ruthie W.", 5, { badge: "founding", city: "Provo, UT" }),
  person("me", "Rosie H.", 3, { badge: "founding", city: "Raleigh, NC" }),
].map(p => [p.id, p]));
export const who = (id: string): Person => PEOPLE[id] ?? person(id, id, 0);

/** The signed-in sample member. She's on the East Coast so local-time
    conversion shows ("7 PM MT · 9 PM your time"). */
export const ME = {
  ...PEOPLE.me,
  first: "Rosie",
  email: "rosie.h@example.com",
  tz: "America/New_York",
  tzAbbr: "ET",
  plan: "member" as Plan,
  memberSince: "February 2025",
  founding: true,
  nextBilling: new Date(Date.UTC(2026, 10, 3, 15)),
  /** Last visit before today: drives "New since you last visited". */
  lastVisit: new Date(denverEvening(2026, 9, 8).getTime() + 2 * 36e5), // Thu Oct 8, 9 PM MT
  notify: { email: true, push: true, dayBefore: true, fifteenMin: true },
};
export type Viewer = typeof ME & { role: Role };

/** Who is looking for a review role. Visitors get null (the UI still shows
    times in ME.tz so the conversion is visible; see viewerTz). */
export function viewer(role: Role): Viewer | null {
  if (role === "visitor") return null;
  if (role === "chelsea") return { ...ME, ...CHELSEA, first: "Chelsea", email: "chelsea@example.com", tz: DENVER, tzAbbr: "MT", plan: "member", founding: false, memberSince: "2019", role };
  return { ...ME, plan: role, role };
}
export const DENVER = "America/Denver";
/** The time zone to show "your time" in. Chelsea sees Mountain only. */
export const viewerTz = (role: Role) => (role === "chelsea" ? DENVER : ME.tz);

/* ─────────────────────────── Prototype clock ─────────────────────────── */

export type Clock = "upcoming" | "tonight" | "live" | "processing" | "replay";
const C1 = denverEvening(2026, 9, 13).getTime(); // Tue Oct 13 2026, 7 PM MT
const min = 6e4, hr = 36e5;
/** Each clock setting is a simulated "now" around tonight's Witch Hat Class 1. */
export const CLOCKS: { key: Clock; label: string; note: string; at: Date }[] = [
  { key: "upcoming", label: "Upcoming", note: "Fri Oct 9, 11 AM MT", at: new Date(C1 - 4 * 24 * hr - 8 * hr) },
  { key: "tonight", label: "Class tonight", note: "Tue Oct 13, 2:12 PM MT", at: new Date(C1 - 4 * hr - 48 * min) },
  { key: "live", label: "Live now", note: "Tue Oct 13, 7:48 PM MT", at: new Date(C1 + 48 * min) },
  { key: "processing", label: "Processing", note: "Tue Oct 13, 10:24 PM MT", at: new Date(C1 + 3 * hr + 24 * min) },
  { key: "replay", label: "Replay ready", note: "Wed Oct 14, 9:05 AM MT", at: new Date(C1 + 14 * hr + 5 * min) },
];
export const DEFAULT_ROLE: Role = "member";
export const DEFAULT_CLOCK: Clock = "tonight";
export const clockAt = (c: Clock) => CLOCKS.find(x => x.key === c)!.at;
export const isRole = (s: string | null): s is Role => !!s && s in RANK;
export const isClock = (s: string | null): s is Clock => !!s && CLOCKS.some(c => c.key === s);

/* ─────────────────────────── Projects ─────────────────────────── */

export type Item = { id: string; name: string; qty?: string; note?: string; url?: string; optional?: boolean };
export type Erratum = { id: string; sessionId?: string; t?: number; text: string };
export type ProjectKind = "garment" | "bag" | "hat" | "quilt";
export type Project = {
  id: string;
  /** "2026-10" */
  month: string;
  /** "October 2026" */
  monthLabel: string;
  title: string;
  /** Pattern name in full, e.g. "Spellbound Quilted Witch Hat". */
  subtitle: string;
  kind: ProjectKind;
  level: "Beginner" | "Confident beginner" | "Intermediate";
  hero: string;
  heroAlt: string;
  /** One or two public sentences (shown to visitors above the paywall). */
  teaser: string;
  /** Chelsea's longer intro, in her voice. */
  about: string[];
  pattern: { name: string; designer: string; url: string; price: string; code?: string; discount?: string; codeExpires?: Date; free?: boolean };
  kitUrl?: string;
  supplies: { pattern: Item[]; chelseaUsed: Item[] };
  /** "Read before you start" corrections, with replay timestamps when there is one. */
  errata: Erratum[];
  sessionIds: string[];
  postIds: string[];
  status: "announced" | "current" | "past";
};

const s = (t: string) => t.split(":").map(Number).reduce((a, n) => a * 60 + n, 0);

export const PROJECTS: Project[] = [
  {
    id: "holiday-stocking", month: "2026-11", monthLabel: "November 2026", title: "Holiday Stocking", subtitle: "Quilted Patchwork Stocking",
    kind: "quilt", level: "Confident beginner", hero: "/img/products/qs-mat.jpg", heroAlt: "A stack of folded quilting cotton on a cutting mat",
    teaser: "A quilted, lined stocking with a cuff and hanging loop. Scrap-friendly, and done in two evenings.",
    about: ["Supply list and pattern code go up the first week of November. Start saving your Christmas scraps!"],
    pattern: { name: "Patchwork Stocking (PDF)", designer: "Chelsea's own pattern", url: "#", price: "Free for members", free: true },
    supplies: { pattern: [], chelseaUsed: [] }, errata: [], sessionIds: [], postIds: [], status: "announced",
  },
  {
    id: "witch-hat", month: "2026-10", monthLabel: "October 2026", title: "Witch Hat", subtitle: "Spellbound Quilted Witch Hat",
    kind: "hat", level: "Confident beginner", hero: "/chelsea-and-cosmo.jpg", heroAlt: "Chelsea at her sewing machine with Cosmo the cat",
    teaser: "A quilted, structured witch hat with a wide brim and bias-bound edge. Patchwork it, appliqué it, or keep it one fabric.",
    about: [
      "I just finished my 2nd witch hat and I might like it even better than my first! One fabric instead of patchwork, with appliquéd spiders all around.",
      "I'm not a huge fan of the pattern's instructions, but I've worked out a method that gives a beautiful result, and I'll show you in class. You still need the pattern for its paper template.",
      "Before class: have your fabric quilted and ready to cut. Iron the interfacing onto the brim before you quilt it.",
    ],
    pattern: { name: "The Spellbound Quilted Witch Hat (PDF)", designer: "Polka Dot Chair", url: "https://shop.polkadotchair.com/products/the-spellbound-quilted-witch-hat-sewing-pattern-digital-pdf-pattern",
      price: "$12", code: "SPELLBOUND25", discount: "25% off", codeExpires: new Date(Date.UTC(2026, 9, 16, 5, 59)) },
    supplies: {
      pattern: [
        { id: "wh-p1", name: "Pellon Flex Foam, 20\" wide", qty: "1½ yd" },
        { id: "wh-p2", name: "Heavyweight single-sided fusible interfacing (20\"+ wide)", qty: "¾ yd" },
        { id: "wh-p3", name: "Hat fabric (top & brim)", qty: "¾ yd" },
        { id: "wh-p4", name: "Hat lining", qty: "¾ yd" },
        { id: "wh-p5", name: "Bias binding fabric", qty: "½ yd" },
        { id: "wh-p6", name: "Sash", qty: "1 fat quarter", optional: true },
        { id: "wh-p7", name: "Printed pattern template", qty: "1", note: "Print at 100%; check the 1\" square" },
        { id: "wh-p8", name: "Bias tape maker", qty: "1", note: "1\" (25 mm) size" },
        { id: "wh-p9", name: "Wonder clips", qty: "~20" },
      ],
      chelseaUsed: [
        { id: "wh-c1", name: "Soft-n-Stable (left over from the cooler bags)", qty: "20\" square", note: "Brim only" },
        { id: "wh-c2", name: "Pellon SF101 Shape-Flex", qty: "¾ yd", note: "Instead of heavyweight interfacing" },
        { id: "wh-c3", name: "Scrap of quilt batting", qty: "~18\" square", note: "For a softer, slouchier top" },
        { id: "wh-c4", name: "Hat fabric", qty: "½ yd", note: "1 yd if it's also your binding" },
        { id: "wh-c5", name: "Hat lining", qty: "½ yd" },
        { id: "wh-c6", name: "Bias binding fabric", qty: "½ yd", note: "Leaves good scraps for the sash", optional: true },
        { id: "wh-c7", name: "Felt for appliqué spiders", qty: "Scraps", optional: true },
      ],
    },
    errata: [
      { id: "wh-e1", sessionId: "witch-hat-c1", t: s("1:12:40"), text: "Trim the cone seam allowance to ¼\" before turning. I said ½\" on the night; ½\" leaves a lump at the tip." },
      { id: "wh-e2", sessionId: "witch-hat-c1", t: s("2:11:05"), text: "Join your bias strips into one long strip BEFORE you press them through the tape maker." },
    ],
    sessionIds: ["witch-hat-prep", "witch-hat-c1", "witch-hat-c2"], postIds: ["three-witches", "hat-fabric"], status: "current",
  },
  {
    id: "skyline", month: "2026-09", monthLabel: "September 2026", title: "Skyline Dress", subtitle: "Skyline Dress",
    kind: "garment", level: "Confident beginner", hero: "/chelsea-rainbow-portrait.jpg", heroAlt: "Chelsea in a plaid dress in front of a rainbow fabric wall",
    teaser: "The easiest dress to throw on and go: bias-bound neckline, in-seam pockets, and a tiered skirt.",
    about: ["Let's get ready to rumble! It's the easiest dress to throw on and go about your day. Bias tape makers are a big help; most quilt shops carry them."],
    pattern: { name: "Skyline Dress (PDF)", designer: "Syd Graham", url: "https://sydgraham.com/collections/sewing-patterns/products/skyline-dress-sewing-pattern", price: "$14",
      code: "SKYLINE20", discount: "20% off", codeExpires: new Date(Date.UTC(2026, 8, 30, 5, 59)) },
    supplies: {
      pattern: [
        { id: "sk-p1", name: "Woven fabric (linen, cotton, gauze)", qty: "2¾–3½ yd", note: "By size; see chart" },
        { id: "sk-p2", name: "Pocket bag fabric", qty: "⅜ yd" },
        { id: "sk-p3", name: "Fabric for 1\" bias binding", qty: "½ yd" },
        { id: "sk-p4", name: "Lightweight fusible interfacing", qty: "¼ yd" },
        { id: "sk-p5", name: "Bias tape maker, 1\"", qty: "1" },
      ],
      chelseaUsed: [
        { id: "sk-c1", name: "Yarn-dyed plaid cotton", qty: "3 yd" },
        { id: "sk-c2", name: "Self-fabric for pockets & binding", qty: "from the 3 yd" },
        { id: "sk-c3", name: "Serger (or a zigzag stitch)", qty: "" },
      ],
    },
    errata: [
      { id: "sk-e1", sessionId: "skyline-c1", t: s("0:38:20"), text: "Attach your bias strips together before you iron them. I meant to fix this at the start of class and didn't." },
      { id: "sk-e2", sessionId: "skyline-c1", t: s("1:47:05"), text: "Serge or zigzag all 4 pocket bag pieces individually. Don't serge the pocket bags together, as I said on the night." },
    ],
    sessionIds: ["skyline-prep", "skyline-c1", "skyline-c2"], postIds: ["september-class"], status: "past",
  },
  {
    id: "color-block", month: "2026-08", monthLabel: "August 2026", title: "Color Block Dress", subtitle: "Colorblock Top & Dress",
    kind: "garment", level: "Beginner", hero: "/chelsea.JPG", heroAlt: "Chelsea at a sewing expo in a printed top",
    teaser: "A free pattern and a great first dress: three color blocks, a simple bodice, and a gathered skirt.",
    about: ["The pattern is free, so this is a great one to try a new fabric combo on."],
    pattern: { name: "Colorblock Set (PDF)", designer: "Matchy Matchy Sewing Club", url: "https://matchymatchysewingclub.com/products/colorblock-set-pdf-sewing-pattern-free", price: "Free", free: true },
    supplies: {
      pattern: [
        { id: "cb-p1", name: "Fabric A (bodice)", qty: "1 yd" },
        { id: "cb-p2", name: "Fabric B (middle band)", qty: "¾ yd" },
        { id: "cb-p3", name: "Fabric C (skirt)", qty: "1½ yd" },
        { id: "cb-p4", name: "Elastic, ¼\"", qty: "1 yd" },
      ],
      chelseaUsed: [{ id: "cb-c1", name: "Three linen-blend solids", qty: "3¼ yd total" }],
    },
    errata: [], sessionIds: ["color-block-prep", "color-block-c1", "color-block-c2"], postIds: ["august-thoughts"], status: "past",
  },
  {
    id: "cooler-bag", month: "2026-07", monthLabel: "July 2026", title: "Cooler Bag", subtitle: "Insulated Cooler Tote",
    kind: "bag", level: "Intermediate", hero: "/video/live-class.jpg", heroAlt: "Chelsea and a guest sewing at a long table during a live class",
    teaser: "An insulated tote that keeps a picnic cold: Soft-n-Stable, a zipper top, and a wipeable lining.",
    about: ["Summer picnics, here we come."],
    pattern: { name: "Cooler Tote (PDF)", designer: "Sample Pattern Co.", url: "#", price: "$10" },
    supplies: {
      pattern: [
        { id: "co-p1", name: "Outer fabric (canvas or quilting cotton)", qty: "1 yd" },
        { id: "co-p2", name: "Insul-Bright", qty: "1 yd" },
        { id: "co-p3", name: "Soft-n-Stable", qty: "1 yd" },
        { id: "co-p4", name: "Wipeable lining (PUL or laminate)", qty: "1 yd" },
        { id: "co-p5", name: "Zipper, 22\"", qty: "1" },
      ],
      chelseaUsed: [],
    },
    errata: [], sessionIds: ["cooler-bag-prep", "cooler-bag-c1", "cooler-bag-c2"], postIds: [], status: "past",
  },
  {
    id: "sunny-side-tank", month: "2026-06", monthLabel: "June 2026", title: "Sunny Side Tank", subtitle: "Sunny Side Tank Top",
    kind: "garment", level: "Beginner", hero: "/concept/gingham-top.jpg", heroAlt: "Close-up of a blue gingham top with puff sleeves",
    teaser: "A breezy summer tank with a bias-faced neckline. A perfect first garment.",
    about: ["Summer's first make!"],
    pattern: { name: "Sunny Side Tank (PDF)", designer: "Matchy Matchy Sewing Club", url: "#", price: "$10", code: "SUNNY20", discount: "20% off", codeExpires: new Date(Date.UTC(2026, 5, 30)) },
    supplies: { pattern: [{ id: "ss-p1", name: "Light woven fabric", qty: "1½ yd" }, { id: "ss-p2", name: "Bias binding", qty: "¼ yd" }], chelseaUsed: [] },
    errata: [], sessionIds: ["sunny-side-tank-prep", "sunny-side-tank-c1", "sunny-side-tank-c2"], postIds: [], status: "past",
  },
];
export const getProject = (id: string | undefined) => PROJECTS.find(p => p.id === id);
export const PROJECT_KINDS: { key: ProjectKind; label: string }[] = [
  { key: "garment", label: "Garments" }, { key: "bag", label: "Bags" }, { key: "hat", label: "Hats" }, { key: "quilt", label: "Quilted" },
];

/** Supply items the sample member has already ticked off (6 of 9 for Witch Hat). */
export const MY_SUPPLIES: Record<string, string[]> = {
  "witch-hat": ["wh-p1", "wh-p2", "wh-p3", "wh-p4", "wh-p7", "wh-p9", "wh-c4", "wh-c5"],
  skyline: ["sk-p1", "sk-p2", "sk-p3", "sk-p4", "sk-p5"],
};

/* ─────────────────────────── Sessions ─────────────────────────── */

export type SessionKind = "club" | "youtube";
export type SessionState = "upcoming" | "tonight" | "live" | "processing" | "replay";
export type Chapter = { t: number; label: string };
export type FixNote = { t: number; text: string };
export type ChatLine = { t: number; who: string; text: string };
export type Replay = {
  /** Seconds. */
  length: number;
  poster: string;
  chapters: Chapter[];
  fixNotes: FixNote[];
  chatReplay: ChatLine[];
  /** Live-chat stats from the night. */
  chatters?: number;
  messages?: number;
};
export type Session = {
  id: string;
  projectId?: string;
  kind: SessionKind;
  /** "Prep" | "Class 1" | "Class 2" | "Free live" */
  label: string;
  /** Full name, e.g. "Witch Hat · Class 1" or a YouTube live's title. */
  title: string;
  /** What we'll do. */
  blurb: string;
  start: Date;
  /** Planned length in minutes. */
  minutes: number;
  /** Pre-recorded (prep videos): goes straight to replay at `start`. */
  recorded?: boolean;
  url?: string;
  replay?: Replay;
  /** Seconds of the replay the sample member has watched. */
  progress?: number;
};

const chat = (lines: [string, string, string][]): ChatLine[] => lines.map(([t, w, text]) => ({ t: s(t), who: w, text }));
const POSTER = "/video/live-class.jpg";
/** Generic chapters scaled to a replay's length, for older classes. */
function roughChapters(len: number, labels: string[]): Chapter[] {
  return labels.map((label, i) => ({ t: i === 0 ? 0 : Math.round((len * i) / labels.length / 60) * 60, label }));
}

/* Club sessions come from the real schedule rule (2nd Tuesday + the Thursday
   after, 7 PM MT) via livesInMonth; each month maps to its project. */
const MONTH_PROJECT: Record<string, string> = {
  "2026-06": "sunny-side-tank", "2026-07": "cooler-bag", "2026-08": "color-block", "2026-09": "skyline", "2026-10": "witch-hat", "2026-11": "holiday-stocking",
};
const ym = (d: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: DENVER, year: "numeric", month: "2-digit" }).format(d).slice(0, 7);
const dkey = (d: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: DENVER, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);

/** Per-session detail that the schedule rule can't know. */
const DETAIL: Record<string, Partial<Session>> = {
  "witch-hat-prep": {
    blurb: "What to quilt before Tuesday, how to fuse the brim, and a look at both of my hats.",
    progress: s("11:42"),
    replay: { length: s("1:04:18"), poster: POSTER, chatters: 0, messages: 0, fixNotes: [],
      chapters: [{ t: 0, label: "Both hats" }, { t: s("6:10"), label: "Supply lists" }, { t: s("14:30"), label: "Fusing the brim" }, { t: s("27:45"), label: "Quilting the panels" }, { t: s("49:20"), label: "Printing the template" }],
      chatReplay: [] },
  },
  "witch-hat-c1": {
    blurb: "Cut the brim and cone, sew the cone, and attach the brim the Chelsea way. Bias binding if we get there!",
    progress: 0,
    replay: {
      length: s("3:04:52"), poster: POSTER, chatters: 14, messages: 386,
      chapters: [
        { t: 0, label: "Hello & show-and-tell" }, { t: s("6:40"), label: "Quilting check" }, { t: s("18:25"), label: "Cutting" },
        { t: s("41:10"), label: "Brim" }, { t: s("1:05:30"), label: "Cone seams" }, { t: s("1:32:00"), label: "Attaching the brim" },
        { t: s("2:05:15"), label: "Bias binding" }, { t: s("2:38:40"), label: "Hem & finishing" }, { t: s("2:55:00"), label: "Q&A + Thursday homework" },
      ],
      fixNotes: [
        { t: s("1:12:40"), text: "Trim the cone seam allowance to ¼\", not ½\" as I said here." },
        { t: s("2:11:05"), text: "Join your bias strips before pressing them through the tape maker." },
      ],
      chatReplay: chat([
        ["0:00:40", "maggie", "Hi from Boise!! 🧙‍♀️"], ["0:01:05", "tasha", "Made it! Kids are in bed"], ["0:01:30", "dee", "Hiii Chelsea and Cosmo"],
        ["0:03:10", "me", "Hello from Raleigh! 9 PM here, let's go"], ["0:05:55", "bev", "My spider fabric came yesterday just in time"],
        ["0:07:20", "linh", "Is it ok that I quilted in straight lines instead of diamonds?"], ["0:07:48", "chelsea", "Totally fine Linh! Any quilting works"],
        ["0:19:02", "carmen", "Do we cut the brim on the fold?"], ["0:19:40", "chelsea", "Yes! Fold first, template on the fold line"],
        ["0:42:15", "jill", "My interfacing is bubbling a little"], ["0:43:00", "chelsea", "Press longer with steam and a pressing cloth, Jill"],
        ["1:06:10", "noor", "The cone looks so good already"], ["1:12:58", "paula", "1/2 inch seam allowance?"], ["1:13:20", "maggie", "That's what she said 😂"],
        ["1:33:30", "tasha", "OH this is so much easier than the pattern's way"], ["1:48:00", "ruthie", "Pinning like my life depends on it"],
        ["2:06:12", "dee", "Bias tape maker squad 🙌"], ["2:12:40", "linh", "Mine came out in 3 pieces lol"], ["2:13:05", "chelsea", "Join them first! I'll put a note on the replay"],
        ["2:40:30", "bev", "Can't believe it's almost done"], ["2:56:10", "me", "Thank you!! See you Thursday"], ["2:57:00", "carmen", "Best class yet"],
      ]),
    },
  },
  "witch-hat-c2": { blurb: "Finish the binding, add the sash, and show-and-tell. Bring your hat, finished or not!" },
  "skyline-prep": {
    blurb: "Cutting layout, bias strips, and which size to make.", progress: s("1:41"),
    replay: { length: s("57:03"), poster: POSTER, chatters: 0, messages: 0, fixNotes: [], chatReplay: [],
      chapters: [{ t: 0, label: "Hello" }, { t: s("4:00"), label: "Choosing a size" }, { t: s("15:30"), label: "Cutting layout" }, { t: s("38:10"), label: "Bias strips" }] },
  },
  "skyline-c1": {
    blurb: "Bodice, binding, pockets and skirt.", progress: s("2:31:28"),
    replay: {
      length: s("3:26:50"), poster: POSTER, chatters: 11, messages: 340,
      chapters: [
        { t: 0, label: "Hello" }, { t: s("12:00"), label: "Bodice" }, { t: s("38:20"), label: "Bias binding" }, { t: s("1:21:00"), label: "Neckline" },
        { t: s("1:47:05"), label: "Pockets" }, { t: s("2:24:30"), label: "Skirt tiers" }, { t: s("3:10:00"), label: "Q&A" },
      ],
      fixNotes: [
        { t: s("0:38:20"), text: "Attach your bias strips together before ironing." },
        { t: s("1:47:05"), text: "Serge or zigzag each of the 4 pocket bag pieces on its own; don't serge them together." },
      ],
      chatReplay: chat([["0:01:00", "maggie", "Plaid matching anxiety 😅"], ["0:39:30", "tasha", "Wait, join them first?"], ["1:48:00", "dee", "Serging now!"], ["3:11:00", "bev", "Loved this one"]]),
    },
  },
  "skyline-c2": {
    blurb: "Hem, finishing and show-and-tell.", progress: 0,
    replay: { length: s("2:48:47"), poster: POSTER, chatters: 9, messages: 212, fixNotes: [], chatReplay: [],
      chapters: roughChapters(s("2:48:47"), ["Hello", "Skirt gathers", "Joining skirt to bodice", "Hem", "Show-and-tell"]) },
  },
  "color-block-prep": { blurb: "Picking three fabrics that play nicely.", progress: s("33:19"),
    replay: { length: s("33:19"), poster: POSTER, chapters: roughChapters(s("33:19"), ["Hello", "Fabric picks", "Cutting"]), fixNotes: [], chatReplay: [] } },
  "color-block-c1": { blurb: "Bodice and color blocks.", progress: s("2:43:43"),
    replay: { length: s("2:43:43"), poster: POSTER, chatters: 12, messages: 288, chapters: roughChapters(s("2:43:43"), ["Hello", "Bodice", "Color blocks", "Gathers", "Q&A"]), fixNotes: [], chatReplay: [] } },
  "color-block-c2": { blurb: "Skirt, elastic and hem.", progress: s("1:58:00"),
    replay: { length: s("2:10:12"), poster: POSTER, chatters: 10, messages: 190, chapters: roughChapters(s("2:10:12"), ["Hello", "Skirt", "Elastic", "Hem"]), fixNotes: [], chatReplay: [] } },
  "cooler-bag-prep": { blurb: "Cutting the insulation layers.", progress: s("28:33"),
    replay: { length: s("28:33"), poster: POSTER, chapters: roughChapters(s("28:33"), ["Hello", "Layers", "Cutting"]), fixNotes: [], chatReplay: [] } },
  "cooler-bag-c1": { blurb: "Outer, insulation and lining.", progress: s("2:55:10"),
    replay: { length: s("2:55:10"), poster: POSTER, chatters: 13, messages: 301, chapters: roughChapters(s("2:55:10"), ["Hello", "Quilting the outer", "Lining", "Zipper", "Q&A"]), fixNotes: [], chatReplay: [] } },
  "cooler-bag-c2": { blurb: "Zipper top and handles.", progress: s("2:20:00"),
    replay: { length: s("2:20:00"), poster: POSTER, chatters: 9, messages: 176, chapters: roughChapters(s("2:20:00"), ["Hello", "Handles", "Boxed corners", "Finishing"]), fixNotes: [], chatReplay: [] } },
  "sunny-side-tank-prep": { blurb: "Fabric and sizing.", progress: s("28:33"),
    replay: { length: s("28:33"), poster: POSTER, chapters: roughChapters(s("28:33"), ["Hello", "Sizing", "Cutting"]), fixNotes: [], chatReplay: [] } },
  "sunny-side-tank-c1": { blurb: "Shoulders, side seams, bias facing.", progress: s("2:30:00"),
    replay: { length: s("2:30:00"), poster: POSTER, chatters: 12, messages: 240, chapters: roughChapters(s("2:30:00"), ["Hello", "Seams", "Bias facing", "Q&A"]), fixNotes: [], chatReplay: [] } },
  "sunny-side-tank-c2": { blurb: "Hem and finishing.", progress: s("1:50:00"),
    replay: { length: s("1:50:00"), poster: POSTER, chatters: 8, messages: 120, chapters: roughChapters(s("1:50:00"), ["Hello", "Armholes", "Hem"]), fixNotes: [], chatReplay: [] } },
  "holiday-stocking-prep": { blurb: "Scraps, piecing and quilting the stocking panels." },
  "holiday-stocking-c1": { blurb: "Piecing, quilting and the cuff." },
  "holiday-stocking-c2": { blurb: "Lining, hanging loop and finishing." },
};

/** Titles for the free YouTube lives (other Tuesdays). */
const YT_TITLES: Record<string, string> = {
  "2026-08-04": "Scrap bin rescue", "2026-08-25": "Answering your machine questions", "2026-09-01": "Back-to-school sewing",
  "2026-09-15": "Market prep sew-along", "2026-09-22": "Fabric haul + chat", "2026-10-06": "Market prep, part two",
  "2026-10-27": "Spooky scrap busting", "2026-11-03": "Gift sewing ideas", "2026-11-17": "Ornament sew-along", "2026-11-24": "Holiday market prep",
};

function buildSessions(): Session[] {
  const out: Session[] = [];
  for (let m = 5; m <= 10; m++) {
    const lives = livesInMonth(2026, m);
    const month = ym(lives[0]?.start ?? new Date(Date.UTC(2026, m, 15)));
    const pid = MONTH_PROJECT[month];
    const project = PROJECTS.find(p => p.id === pid);
    let n = 0;
    for (const l of lives) {
      if (l.kind === "club" && project) {
        n++;
        const id = `${pid}-c${n}`;
        out.push({ id, projectId: pid, kind: "club", label: `Class ${n}`, title: `${project.title} · Class ${n}`, blurb: "",
          start: l.start, minutes: n === 1 ? 180 : 150, ...DETAIL[id] });
      } else if (l.kind === "youtube" && m >= 7) {
        const k = dkey(l.start);
        out.push({ id: `yt-${k}`, kind: "youtube", label: "Free live", title: YT_TITLES[k] ?? "Tuesday sew & chat",
          blurb: "Free for everyone on YouTube. Bring whatever you're working on.", start: l.start, minutes: 90, url: YOUTUBE_LIVE_URL });
      }
    }
    if (project) {
      // Prep videos are recorded and posted the week before Class 1.
      const c1 = out.find(x => x.id === `${pid}-c1`)!;
      const id = `${pid}-prep`;
      out.push({ id, projectId: pid, kind: "club", label: "Prep", title: `${project.title} · Prep`, blurb: "", recorded: true,
        start: new Date(c1.start.getTime() - 5 * 24 * hr - 7 * hr), minutes: 60, ...DETAIL[id] });
    }
  }
  return out.sort((a, b) => a.start.getTime() - b.start.getTime());
}
export const SESSIONS: Session[] = buildSessions();
export const getSession = (id: string | undefined) => SESSIONS.find(x => x.id === id);
/** A project's sessions in class order (Prep → Class 1 → Class 2). */
export const sessionsFor = (projectId: string) => SESSIONS.filter(x => x.projectId === projectId);
export const projectOf = (x: Session) => getProject(x.projectId);
// Fill sessionIds for projects whose sessions were generated.
for (const p of PROJECTS) if (!p.sessionIds.length) p.sessionIds = sessionsFor(p.id).map(x => x.id);

/* ─────────────────────────── Session state ─────────────────────────── */

/** How long after a class ends before the replay is up. */
export const PROCESSING_HOURS = 6;
export const sessionEnd = (x: Session) => new Date(x.start.getTime() + x.minutes * min);
/** Denver calendar day ("2026-10-13"), to tell "tonight" from "upcoming". */
export const denverDay = dkey;

/** The state of a session at a (simulated) instant:
    - upcoming: before start, on another day
    - tonight: before start, same Mountain-time calendar day
    - live: between start and planned end
    - processing: ended less than PROCESSING_HOURS ago (club only)
    - replay: after that (recorded prep videos go straight here at `start`) */
export function sessionState(x: Session, now: Date): SessionState {
  const t = now.getTime(), a = x.start.getTime(), b = sessionEnd(x).getTime();
  if (t < a) return dkey(now) === dkey(x.start) ? "tonight" : "upcoming";
  if (x.recorded) return "replay";
  if (t < b) return "live";
  if (x.kind === "club" && t < b + PROCESSING_HOURS * hr) return "processing";
  return "replay";
}
export const STATE_LABEL: Record<SessionState, string> = {
  upcoming: "Upcoming", tonight: "Tonight", live: "Live now", processing: "Replay processing", replay: "Replay ready",
};
/** Is the replay watchable now (state replay and it has a video)? */
export const hasReplay = (x: Session, now: Date) => sessionState(x, now) === "replay" && !!x.replay;

/** The club or YouTube session live at `now`, if any (club first). */
export function liveSession(now: Date, kind?: SessionKind): Session | undefined {
  const live = SESSIONS.filter(x => sessionState(x, now) === "live" && (!kind || x.kind === kind));
  return live.find(x => x.kind === "club") ?? live[0];
}
/** Sessions that haven't started yet, soonest first. */
export function upcomingSessions(now: Date, count = 6, kind?: SessionKind): Session[] {
  return SESSIONS.filter(x => x.start > now && !x.recorded && (!kind || x.kind === kind)).slice(0, count);
}
/** The next club class that isn't over (live counts). */
export function nextClubSession(now: Date): Session | undefined {
  return SESSIONS.find(x => x.kind === "club" && !x.recorded && sessionEnd(x) > now);
}

/** What the Home "Next up" card should feature, in priority order:
    live → processing → class tonight → replay posted in the last 36h → next upcoming. */
export function heroSession(now: Date): { session: Session; state: SessionState } | undefined {
  const club = SESSIONS.filter(x => x.kind === "club" && !x.recorded);
  const pick = (st: SessionState) => club.find(x => sessionState(x, now) === st);
  const live = pick("live"); if (live) return { session: live, state: "live" };
  const proc = pick("processing"); if (proc) return { session: proc, state: "processing" };
  const tonight = pick("tonight"); if (tonight) return { session: tonight, state: "tonight" };
  const fresh = club.filter(x => x.replay && sessionState(x, now) === "replay" && now.getTime() - sessionEnd(x).getTime() < 36 * hr).pop();
  if (fresh) return { session: fresh, state: "replay" };
  const next = club.find(x => x.start > now);
  return next ? { session: next, state: sessionState(next, now) } : undefined;
}

/** This month's project (by Mountain-time month), else the latest non-announced one. */
export function currentProject(now: Date): Project {
  return PROJECTS.find(p => p.month === ym(now) && p.status !== "announced") ?? PROJECTS.find(p => p.status === "current")!;
}
/** Projects with at least one session that has started, newest first. */
export const pastProjects = (now: Date) => PROJECTS.filter(p => p.month < ym(now));

/** Resume rows: replays the member has started but not finished, most recent first. */
export function continueWatching(now: Date): Session[] {
  return SESSIONS.filter(x => x.replay && hasReplay(x, now) && (x.progress ?? 0) > 0 && (x.progress ?? 0) < x.replay.length - 30)
    .sort((a, b) => b.start.getTime() - a.start.getTime());
}
/** Total replay runtime of a project at `now`, and how much the member has watched (seconds). */
export function projectRuntime(p: Project, now: Date) {
  const ss = sessionsFor(p.id).filter(x => x.replay && hasReplay(x, now));
  const total = ss.reduce((a, x) => a + x.replay!.length, 0);
  const watched = ss.reduce((a, x) => a + Math.min(x.progress ?? 0, x.replay!.length), 0);
  return { total, watched, ratio: total ? watched / total : 0, classes: sessionsFor(p.id).filter(x => x.label !== "Prep").length };
}

/* ─────────────────────────── Notes, posts, makes, Q&A ─────────────────────────── */

export type Note = {
  id: string; at: Date; text: string;
  kind: "logistics" | "reschedule" | "hello" | "replay";
  /** Pinned to the top of every screen until `until` (or dismissed). */
  pinned?: boolean; until?: Date;
  sessionId?: string; projectId?: string;
  /** Who can read it (default: notes = free accounts and up). */
  need?: Role;
};
const at = (day: number, h: number, m = 0, month = 9) => new Date(denverEvening(2026, month, day).getTime() + (h - 19) * hr + m * min);
export const NOTES: Note[] = [
  { id: "n-sick", at: at(9, 9, 0, 7), kind: "reschedule", text: "I've been down with a bug all weekend, so no live today. The cutting video moves to tomorrow. Thanks for understanding!", projectId: "color-block" },
  { id: "n-back", at: at(28, 10, 0, 8), kind: "hello", text: "My witch hat is DONE and I couldn't be happier! Class is Oct 13 & 15 at 7 PM MT. Supply list coming when I'm back from vacation.", projectId: "witch-hat", need: "visitor" },
  { id: "n-supplies", at: at(6, 9, 0), kind: "logistics", text: "Witch Hat supply list and pattern code are up. 25% off with SPELLBOUND25 through Oct 15.", projectId: "witch-hat" },
  { id: "n-prep", at: at(8, 12, 30), kind: "logistics", text: "Prep video is up! Have your fabric quilted (brim interfaced first) before Tuesday. No need to cut pieces yet.", projectId: "witch-hat", sessionId: "witch-hat-prep", pinned: true, until: at(13, 12) },
  { id: "n-tonight", at: at(13, 13, 0), kind: "logistics", text: "See you at 7!! Have your quilted panels, template and bias fabric next to the machine.", sessionId: "witch-hat-c1", pinned: true, until: at(13, 19) },
  { id: "n-golive", at: at(13, 18, 52), kind: "logistics", text: "Going live in a few minutes!!", sessionId: "witch-hat-c1" },
  { id: "n-replay", at: at(14, 8, 40), kind: "replay", text: "Replay's up with chapters. One fix: trim the cone seam to ¼\" (note at 1:12:40). Class 2 is Thursday, same time!", sessionId: "witch-hat-c1", pinned: true, until: at(15, 19) },
];
/** Notes posted by `now`, newest first. */
export const notesAt = (now: Date) => NOTES.filter(n => n.at <= now).sort((a, b) => b.at.getTime() - a.at.getTime());
/** The note pinned at `now`, if any. */
export const pinnedNote = (now: Date) => notesAt(now).find(n => n.pinned && (!n.until || now < n.until));

export type Post = {
  id: string; title: string; at: Date; projectId?: string; cover?: string;
  /** Minimum role to read it ("free" posts are the upsell teasers). */
  need: Role;
  minutes: number; excerpt: string; body: string[]; tags: string[];
  likes: number; comments: number;
};
export const POSTS: Post[] = [
  { id: "welcome", title: "Welcome to the new club home!", at: at(1, 10), need: "free", minutes: 3, cover: "/img/chelsea.jpeg",
    excerpt: "Everything for each month's project now lives in one place: supplies, the pattern code, both classes and your makes.",
    body: ["Hi friends! We moved off Patreon and into our own little home. Each month gets one project page with the supply list, the pattern and code, the prep video, both classes and everyone's makes.", "Class times now show in your own time zone, and you can add them to your calendar with one tap.", "Thank you for coming along, I'm so excited!"],
    tags: ["club"], likes: 41, comments: 12 },
  { id: "three-witches", title: "Three witches & pattern designing", at: at(2, 15), projectId: "witch-hat", need: "friend", minutes: 4, cover: "/concept/label.jpg",
    excerpt: "The Three Witches desk pal tester call is this week, and a peek at how I draft a pattern from a sketch.",
    body: ["The Three Witches Desk Pal tester call is happening this week!", "Here's a peek at my sketchbook and how a pattern goes from doodle to PDF."], tags: ["studio"], likes: 18, comments: 6 },
  { id: "hat-fabric", title: "Picking fabric for a quilted hat", at: at(7, 11), projectId: "witch-hat", need: "member", minutes: 5, cover: "/img/products/qs-mat.jpg",
    excerpt: "Quilting cotton, canvas or velvet? What holds a brim up and what flops.",
    body: ["Quilting cotton is the easy win. Velvet is gorgeous but shifts while you quilt it, so baste generously."], tags: ["tips"], likes: 23, comments: 9 },
  { id: "september-class", title: "September class!", at: at(19, 10, 0, 7), projectId: "skyline", need: "free", minutes: 2, cover: "/chelsea-rainbow-portrait.jpg",
    excerpt: "We're making the Skyline Dress! Here's why I picked it.", body: ["The Skyline Dress is the easiest dress to throw on and go."], tags: ["club"], likes: 30, comments: 8 },
  { id: "august-thoughts", title: "August sew-along thoughts", at: at(20, 9, 0, 7), projectId: "color-block", need: "friend", minutes: 3, cover: "/chelsea.JPG",
    excerpt: "What worked, what didn't, and a poll for what's next.", body: ["Thank you for your notes on the Color Block classes."], tags: ["club"], likes: 14, comments: 11 },
  { id: "england-tour", title: "Dream England trip with me!", at: at(23, 9, 0, 6), need: "visitor", minutes: 4, cover: "/chelsea-rainbow-portrait.jpg",
    excerpt: "Next summer I'm hosting a craft tour through England: textiles, quilts, castles.", body: ["We're talking textiles, quilts, castles, and two full days of classes."], tags: ["travel"], likes: 52, comments: 19 },
];
export const postsFor = (projectId: string) => POSTS.filter(p => p.projectId === projectId);
export const getPost = (id: string | undefined) => POSTS.find(p => p.id === id);

export type Make = { id: string; projectId: string; by: string; img: string; caption: string; at: Date; likes: number; fromClass?: boolean };
export const MAKES: Make[] = [
  { id: "mk1", projectId: "witch-hat", by: "chelsea", img: "/chelsea-and-cosmo.jpg", caption: "Hat #2: one fabric + appliqué spiders 🕷️", at: at(5, 16), likes: 34 },
  { id: "mk2", projectId: "witch-hat", by: "bev", img: "/concept/label.jpg", caption: "Brim quilted and ready!", at: at(11, 14), likes: 9 },
  { id: "mk3", projectId: "witch-hat", by: "tasha", img: "/img/products/qs-mat.jpg", caption: "My scraps for the patchwork version", at: at(12, 20), likes: 7 },
  { id: "mk4", projectId: "witch-hat", by: "linh", img: "/concept/gingham-top.jpg", caption: "Gingham witch?? Gingham witch.", at: at(13, 22, 40), likes: 15, fromClass: true },
  { id: "mk5", projectId: "witch-hat", by: "maggie", img: "/chelsea.JPG", caption: "Cone done, brim tomorrow", at: at(14, 7, 30), likes: 6, fromClass: true },
  { id: "mk6", projectId: "skyline", by: "dee", img: "/chelsea-rainbow-portrait.jpg", caption: "Skyline in plaid, pockets serged the RIGHT way", at: at(14, 12, 0, 8), likes: 21 },
  { id: "mk7", projectId: "skyline", by: "me", img: "/concept/gingham-top.jpg", caption: "Mine! Gingham Skyline for fall", at: at(20, 18, 0, 8), likes: 17 },
  { id: "mk8", projectId: "skyline", by: "ruthie", img: "/concept/label.jpg", caption: "Added my own label", at: at(22, 9, 0, 8), likes: 12 },
  { id: "mk9", projectId: "color-block", by: "carmen", img: "/chelsea.JPG", caption: "Mustard + teal + pink, no regrets", at: at(18, 10, 0, 7), likes: 19 },
  { id: "mk10", projectId: "color-block", by: "jill", img: "/img/products/qs-mat.jpg", caption: "Fabric pull for the dress", at: at(12, 10, 0, 7), likes: 8 },
  { id: "mk11", projectId: "cooler-bag", by: "noor", img: "/video/live-class.jpg", caption: "Picnic ready", at: at(20, 10, 0, 6), likes: 13 },
];
/** Makes posted by `now` (all projects, or one), newest first. */
export const makesAt = (now: Date, projectId?: string) =>
  MAKES.filter(m => m.at <= now && (!projectId || m.projectId === projectId)).sort((a, b) => b.at.getTime() - a.at.getTime());

export type Answer = { by: string; at: Date; text: string };
export type Question = {
  id: string; projectId: string; sessionId?: string; t?: number; by: string; at: Date; text: string;
  answer?: Answer; resolved?: boolean; votes: number; replies?: { by: string; at: Date; text: string }[];
};
export const QA: Question[] = [
  { id: "q1", projectId: "witch-hat", by: "jill", at: at(6, 15), votes: 6, resolved: true, text: "Can I use fusible fleece instead of the flex foam?",
    answer: { by: "chelsea", at: at(6, 18), text: "Yes for the top of the hat! For the brim you want something stiffer: flex foam, Soft-n-Stable, or two layers of fleece." } },
  { id: "q2", projectId: "witch-hat", by: "carmen", at: at(9, 11), votes: 4, resolved: true, text: "Do we need to cut out all the pieces before class?",
    answer: { by: "chelsea", at: at(9, 12, 30), text: "Nope! Just have your fabric quilted. We'll cut together on Tuesday." } },
  { id: "q3", projectId: "witch-hat", by: "paula", at: at(12, 19), votes: 3, text: "What width should the bias strips be: 2\" or 2½\"?",
    replies: [{ by: "maggie", at: at(12, 20), text: "I think the pattern says 2\" for the 1\" maker?" }] },
  { id: "q4", projectId: "witch-hat", sessionId: "witch-hat-c1", t: s("1:32:00"), by: "linh", at: at(14, 7), votes: 2, text: "At 1:32:00, do the brim darts face up or down?" },
  { id: "q5", projectId: "skyline", by: "dee", at: at(10, 9, 0, 8), votes: 5, resolved: true, text: "Where can I find the pattern info to purchase?",
    answer: { by: "chelsea", at: at(16, 9, 0, 8), text: "It's linked at the top of the project page with the code. Here ya go!" } },
  { id: "q6", projectId: "skyline", sessionId: "skyline-c1", t: s("1:47:05"), by: "tasha", at: at(10, 10, 0, 8), votes: 8, resolved: true, text: "Serge the pocket bags together or separately?",
    answer: { by: "chelsea", at: at(10, 12, 0, 8), text: "Separately! All 4 pieces on their own. I fumbled that on the night. Fix note added at 1:47:05." } },
];
/** Q&A for a project at `now`, most votes first, unanswered on top when asked. */
export const qaFor = (projectId: string, now: Date) => QA.filter(q => q.projectId === projectId && q.at <= now).sort((a, b) => b.votes - a.votes);

/** Community channels (Community tab). */
export type Channel = { id: "makes" | "help" | "general"; name: string; about: string };
export const CHANNELS: Channel[] = [
  { id: "makes", name: "show-your-makes", about: "Photos of what you made, by project" },
  { id: "help", name: "help", about: "Stuck? Ask here. Chelsea marks answers" },
  { id: "general", name: "general", about: "Say hi, fabric hauls, everything else" },
];
export type Message = { id: string; channel: Channel["id"]; by: string; at: Date; text: string; reactions?: Record<string, number>; img?: string; replyCount?: number };
export const MESSAGES: Message[] = [
  { id: "g1", channel: "general", by: "maggie", at: at(10, 9), text: "Anyone else going to Stitches Night Out on the 20th?", reactions: { "🙋‍♀️": 4 }, replyCount: 3 },
  { id: "g2", channel: "general", by: "noor", at: at(11, 16), text: "Fabric haul from the quilt shop downtown, the witchy prints are IN", img: "/img/products/qs-mat.jpg", reactions: { "😍": 6 } },
  { id: "g3", channel: "general", by: "chelsea", at: at(12, 10), text: "Cosmo approves of everyone's hats so far 🐈‍⬛", reactions: { "❤️": 11 } },
  { id: "g4", channel: "general", by: "bev", at: at(13, 22, 15), text: "That was SO fun tonight. My hat has a brim!!", reactions: { "🎉": 7 } },
  { id: "h1", channel: "help", by: "paula", at: at(12, 19), text: "Bias strips: 2\" or 2½\" for the witch hat?", replyCount: 1 },
  { id: "h2", channel: "help", by: "ruthie", at: at(13, 9), text: "My machine skips stitches on the foam. Needle size?", replyCount: 2 },
];
export const messagesAt = (now: Date, channel?: Channel["id"]) =>
  MESSAGES.filter(m => m.at <= now && (!channel || m.channel === channel)).sort((a, b) => a.at.getTime() - b.at.getTime());

/** "New since you last visited" for Home: replies, makes, posts and notes after ME.lastVisit. */
export function newSince(now: Date, since: Date = ME.lastVisit) {
  const inRange = (d: Date) => d > since && d <= now;
  return {
    makes: MAKES.filter(m => inRange(m.at)),
    posts: POSTS.filter(p => inRange(p.at)),
    answers: QA.filter(q => q.answer && inRange(q.answer.at)),
    notes: NOTES.filter(n => inRange(n.at)),
  };
}

/* ─────────────────────────── Formatting ─────────────────────────── */

/** "3h 27m" / "57m" from seconds. */
export function fmtDur(sec: number) {
  const h = Math.floor(sec / 3600), m = Math.round((sec % 3600) / 60);
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}
/** "1:12:40" / "52:36" from seconds (player style). */
export function fmtT(sec: number) {
  sec = Math.max(0, Math.round(sec));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), x = sec % 60;
  const p = (n: number) => String(n).padStart(2, "0");
  return h ? `${h}:${p(m)}:${p(x)}` : `${m}:${p(x)}`;
}
/** "52:36 left" from length and progress (seconds). */
export const fmtLeft = (length: number, progress = 0) => `${fmtT(length - progress)} left`;

/** "7 PM" / "7:30 PM" in a time zone. */
export function fmtTime(d: Date, tz: string = DENVER) {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(d);
  return parts.replace(":00", "");
}
/** "Tue, Oct 13" (or other Intl options) in a time zone. */
export const fmtDay = (d: Date, tz: string = DENVER, o: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" }) =>
  new Intl.DateTimeFormat("en-US", { timeZone: tz, ...o }).format(d);
const offsetMin = (d: Date, tz: string) => {
  const p = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
    .formatToParts(d).reduce<Record<string, string>>((a, x) => ((a[x.type] = x.value), a), {});
  return (Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute) - d.getTime()) / 6e4;
};
/** True when two zones show the same wall time at d. */
export const sameZone = (d: Date, tz: string) => offsetMin(d, tz) === offsetMin(d, DENVER);
/** "7 PM MT · 9 PM your time" (or just "7 PM MT" when the viewer is on Mountain time).
    `yours` replaces "your time", e.g. "ET". */
export function timeLabel(d: Date, tz: string = ME.tz, yours = "your time") {
  const mt = `${fmtTime(d, DENVER)} MT`;
  if (sameZone(d, tz)) return mt;
  const dayShift = fmtDay(d, tz, { day: "numeric" }) !== fmtDay(d, DENVER, { day: "numeric" }) ? ` (${fmtDay(d, tz, { weekday: "short" })})` : "";
  return `${mt} · ${fmtTime(d, tz)}${dayShift} ${yours}`;
}
/** "Tonight" / "Tomorrow" / "Thursday" / "Tue, Nov 10" relative to now, in a zone. */
export function relDay(d: Date, now: Date, tz: string = DENVER) {
  const k = (x: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(x);
  const days = Math.round((Date.parse(k(d)) - Date.parse(k(now))) / 864e5);
  if (days === 0) return Number(new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", hourCycle: "h23" }).format(d)) >= 17 ? "Tonight" : "Today";
  if (days === 1) return "Tomorrow";
  if (days === -1) return "Yesterday";
  if (days > 1 && days < 7) return fmtDay(d, tz, { weekday: "long" });
  return fmtDay(d, tz);
}
/** "2h ago" / "3d ago" / "Oct 6". */
export function ago(d: Date, now: Date) {
  const m = Math.round((now.getTime() - d.getTime()) / 6e4);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  if (days < 7) return `${days}d ago`;
  return fmtDay(d, DENVER, { month: "short", day: "numeric" });
}
